import React, { shallowEqual } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocalisation } from '@3plearning/lingui-cdn';

import { assignmentFilterType } from '@helpers/constants';
import Resources from '@helpers/resources';
import useAssignmentResultType from '@helpers/hooks/use-assignment-result-type';
import useCourseVersion from '@helpers/hooks/use-course-version';
import useFetchResource from '@helpers/hooks/use-fetch-resource';
import useManageAssignments from '@helpers/hooks/use-manage-assignments';
import { Select, UnassignIconDark, ReassignIconDark } from '@helpers/icon-dictionary';

import SvgIcon from '@components/svg-icon/svg-icon';

import Attempt from '../attempt';

import getTranslations from '../translations';
import { Heading, SubHeading, ActionWrapper, Wrapper, UnassignWrapper, Attempts, AttemptsText } from './styled';

const ContextualMenu = props => {
  const { selectCell, isCellSelected, studentId, learningJourneyId, hasOnlyResult, assignmentId } = props;
  /**
   * Hooks
   */
  const { i18n } = useLocalisation();
  const selectedProduct = useSelector(state => state.product, shallowEqual);
  const { assignmentResultType } = useAssignmentResultType();
  const { selectedClassroomId, selectedCourseId, selectedCourseVersion } = useCourseVersion();
  const { firstName, lastName } = useSelector(state => state.entitiesReducer.students[studentId]);
  const learningJourneyName = useSelector(state => state.entitiesReducer.learningJourneys[learningJourneyId].name);
  const { unassignAssignments, assignAssignments } = useManageAssignments();

  const { data: attempts = [] } = useFetchResource({
    resourceCallback: () =>
      Resources.studentAssignmentsHistory
        .get({
          productId: selectedProduct.id,
          classId: selectedClassroomId,
          courseId: selectedCourseId,
          courseVersion: selectedCourseVersion,
          studentUserProfileId: studentId,
          learningJourneyId
        })
        .withParams({ assignmentResultType })
        .send(),
    isReady: selectedProduct.id && selectedClassroomId && selectedCourseId
  });

  /**
   * Translations
   */
  const {
    SELECT_CELL_TEXT,
    UNASSIGN_FROM_STUDENT_TEXT,
    REASSIGN_TEXT,
    UNSELECT_CELL_TEXT,
    ASSIGN_AND_REVIEW_TEXT,
    LEARNING_GAPS_TEXT,
    VOLUNTARY_TEXT,
    ATTEMPTS_TEXT
  } = getTranslations({ i18n });

  /**
   * Functions
   */

  const handleReassign = async () => {
    const REASSIGN_ASSIGNMENT = true;
    const studentAssignments = [
      {
        studentUserProfileId: studentId,
        learningJourneyId
      }
    ];
    await assignAssignments({
      studentAssignments,
      isReassignment: REASSIGN_ASSIGNMENT
    });
  };

  const handleUnassign = () => {
    if (!hasOnlyResult) {
      const requestData = [
        {
          learningJourneyId,
          assignmentId,
          studentUserProfileId: studentId
        }
      ];
      unassignAssignments(requestData);
    }
  };

  const getAssignmentOrigin = assignmentAttempts => {
    const { ASSIGN_UPF, ASSIGN_LEARNING_GAPS, VOLUNTARY } = assignmentFilterType;

    const assignmentType = assignmentAttempts.filter(
      assignment => assignment.isFocusedResult
    )[0]?.assignmentType;

    const assignmentOriginsText = {
      [ASSIGN_UPF]: ASSIGN_AND_REVIEW_TEXT,
      [ASSIGN_LEARNING_GAPS]: LEARNING_GAPS_TEXT,
      [VOLUNTARY]: VOLUNTARY_TEXT
    };

    return assignmentOriginsText[assignmentType];
  };

  return (
    <Wrapper>
      <Heading data-testid="student-fullname">{`${firstName} ${lastName}`}</Heading>
      <Heading data-testid="learning-journey-name">{learningJourneyName}</Heading>
      <SubHeading>{getAssignmentOrigin(attempts)}</SubHeading>
      <AttemptsText>{ATTEMPTS_TEXT}:</AttemptsText>
      <Attempts>
        {attempts.map(attempt => (
          <Attempt key={attempt.assignedOn} attempt={attempt} />
        ))}
      </Attempts>
      <ActionWrapper data-testid="select-cell" onClick={selectCell}>
        <SvgIcon icon={Select} />
        {isCellSelected ? UNSELECT_CELL_TEXT : SELECT_CELL_TEXT}
      </ActionWrapper>
      <UnassignWrapper data-testid="unassign-from-student" hasOnlyResult={hasOnlyResult} onClick={handleUnassign}>
        <SvgIcon icon={UnassignIconDark} />
        {UNASSIGN_FROM_STUDENT_TEXT}
      </UnassignWrapper>
      <ActionWrapper data-testid="reassign-assignment" onClick={handleReassign}>
        <SvgIcon icon={ReassignIconDark} />
        {REASSIGN_TEXT}
      </ActionWrapper>
    </Wrapper>
  );
};

ContextualMenu.propTypes = {
  studentId: PropTypes.number.isRequired,
  learningJourneyId: PropTypes.number.isRequired,
  assignmentId: PropTypes.number,
  selectCell: PropTypes.func.isRequired,
  isCellSelected: PropTypes.bool.isRequired,
  hasOnlyResult: PropTypes.bool
};

export default ContextualMenu;



// WEBPACK FOOTER //
// ./components/status-cell/contextual-menu/contextual-menu.js